import type { UserRole } from '@/contexts/AuthContext';
import { normalizeAppRedirect } from '@/lib/access';

const OAUTH_REDIRECT_STORAGE_KEY = 'proptrust_oauth_redirect';
const GOOGLE_SIGNUP_STORAGE_KEY = 'proptrust_google_signup';
const GOOGLE_SIGNUP_INTENT_STORAGE_KEY = 'proptrust_google_signup_intent';

export interface GoogleSignupPayload {
  firstName: string;
  lastName: string;
  phone: string;
  role: UserRole;
  organizationName: string;
  licenseNumber: string;
  termsAccepted: boolean;
  redirectTo: string | null;
}

function safeSessionStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function getOAuthCallbackUrl() {
  return `${window.location.origin}/auth/callback`;
}

export function storeOAuthRedirect(redirectTo?: string | null) {
  const storage = safeSessionStorage();
  if (!storage) return;

  storage.setItem(OAUTH_REDIRECT_STORAGE_KEY, normalizeAppRedirect(redirectTo, '/'));
}

export function consumeOAuthRedirect() {
  const storage = safeSessionStorage();
  if (!storage) return '/';

  const redirectTo = normalizeAppRedirect(storage.getItem(OAUTH_REDIRECT_STORAGE_KEY), '/');
  storage.removeItem(OAUTH_REDIRECT_STORAGE_KEY);
  return redirectTo;
}

export function storeGoogleSignupPayload(payload: GoogleSignupPayload) {
  const storage = safeSessionStorage();
  if (!storage) return;

  storage.setItem(GOOGLE_SIGNUP_STORAGE_KEY, JSON.stringify(payload));
}

export function clearGoogleSignupPayload() {
  const storage = safeSessionStorage();
  if (!storage) return;

  storage.removeItem(GOOGLE_SIGNUP_STORAGE_KEY);
}

export function storeGoogleSignupIntent() {
  const storage = safeSessionStorage();
  if (!storage) return;

  storage.setItem(GOOGLE_SIGNUP_INTENT_STORAGE_KEY, 'true');
}

export function consumeGoogleSignupIntent() {
  const storage = safeSessionStorage();
  if (!storage) return false;

  const hasIntent = storage.getItem(GOOGLE_SIGNUP_INTENT_STORAGE_KEY) === 'true';
  storage.removeItem(GOOGLE_SIGNUP_INTENT_STORAGE_KEY);
  return hasIntent;
}

export function consumeGoogleSignupPayload(): GoogleSignupPayload | null {
  const storage = safeSessionStorage();
  if (!storage) return null;

  const rawPayload = storage.getItem(GOOGLE_SIGNUP_STORAGE_KEY);
  storage.removeItem(GOOGLE_SIGNUP_STORAGE_KEY);

  if (!rawPayload) {
    return null;
  }

  try {
    return JSON.parse(rawPayload) as GoogleSignupPayload;
  } catch {
    return null;
  }
}
